<template>
  <v-container id="contact">
    <v-card flat class="" color="transparent">
      <h2 class="text-h4 text-primary text-center font-weight-bold pb-12">
        {{ title ? title : "Contacto" }}
      </h2>
      <v-form ref="form" v-model="validForm">
        <v-row>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="formFields.name"
              label="Nombre *"
              :rules="[required, max40]"
              aria-label="Nombre del cliente"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="formFields.lastname"
              label="Apellido *"
              :rules="[required, max40]"
              aria-label="Apellido del cliente"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="formFields.email"
              label="Correo *"
              :rules="[required, validEmail]"
              aria-label="Correo del cliente"
            />
          </v-col>

          <!-- <v-col cols="12" sm="6" md="4">
            <v-select
              id="plan-select"
              return-object
              :items="plansItems"
              item-title="name"
              item-value="name"
              label="Plan de interés"
              :rules="[required]"
              :menu-props="{ contentProps: { 'v-lenis-manager': true } }"
              aria-label="Plan de interés del cliente"
            >
              <template #item="{ item, props }">
                <v-list-item v-bind="props">
                  <template #append>
                    <v-chip
                      :color="item.raw.national ? 'primary' : 'secondary'"
                      size="small"
                      variant="outlined"
                    >
                      {{ item.raw.national ? "Nacional" : "Internacional" }}
                    </v-chip>
                  </template>
                </v-list-item>
              </template>
            </v-select>
          </v-col> -->
          <v-col cols="12">
            <v-textarea
              v-model="formFields.message"
              label="Mensaje *"
              rows="1"
              auto-grow
              hide-details="auto"
              :counter="200"
              :rules="[required]"
              aria-label="Mensaje del cliente"
            />
          </v-col>
        </v-row>
      </v-form>
      <v-card-actions class="d-flex justify-center">
        <v-btn
          :color="buttonColor"
          type="button"
          :loading="submitState === 'loading'"
          :disabled="submitState === 'loading'"
          @click="handleSubmit"
          aria-label="Enviar formulario de contacto"
        >
          {{ buttonLabel }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script setup lang="ts">
import { required, validEmail, maxDigits } from "#imports";
import { computed, onBeforeUnmount } from "vue";
// import emailjs from "@emailjs/browser";
const props = defineProps<{
  title?: string | boolean;
}>();

type SubmitState = "idle" | "loading" | "success" | "error";

const form = ref();
const formFields = ref({
  name: "",
  lastname: "",
  email: "",
  message: "",
});
const validForm = ref(false);
const submitState = ref<SubmitState>("idle");
const buttonLabel = computed(() => {
  switch (submitState.value) {
    case "loading":
      return "Enviando…";
    case "success":
      return "Enviado";
    case "error":
      return "Reintentar";
    default:
      return "Enviar";
  }
});

const buttonColor = computed(() => {
  switch (submitState.value) {
    case "success":
      return "success";
    case "error":
      return "error";
    default:
      return "primary";
  }
});

// Helper rule to avoid TS annotations in template
const max40 = (v: unknown) => maxDigits(v as any, 40);
const sendEmail = async () => {
  try {
    const payload = { ...formFields.value };

    await $fetch("/api/contact", {
      method: "POST",
      body: payload,
    });

    form.value?.reset();
    form.value?.resetValidation();
  } catch (error) {
    throw new Error(error as string);
  }
};

let resetTimer: ReturnType<typeof setTimeout> | undefined;

const setSubmitState = (state: SubmitState) => {
  if (resetTimer) {
    clearTimeout(resetTimer);
    resetTimer = undefined;
  }

  submitState.value = state;

  if (state === "success" || state === "error") {
    resetTimer = setTimeout(() => {
      submitState.value = "idle";
    }, 2000);
  }
};

const handleSubmit = async () => {
  if (!form.value || submitState.value === "loading") return;

  const { valid } = await form.value.validate();
  if (!valid) return;

  setSubmitState("loading");

  try {
    await sendEmail();
    setSubmitState("success");
  } catch (error) {
    setSubmitState("error");
    console.error(error);
  }
};

onBeforeUnmount(() => {
  if (resetTimer) {
    clearTimeout(resetTimer);
  }
});
</script>

<style lang="scss" scoped></style>
